import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  useGetMeOrganizations,
  getGetMeOrganizationsQueryKey,
  postMeOrganizationsInvitationsAccept,
  postMeOrganizationsInvitationsDecline,
} from '@/api/auth/self/self';
import {
  OrganizationMembership,
  OrganizationMembershipRole,
} from '@/api/auth/missionImpactAuthAPI.schemas';
import { postOrganizationProductRegistrations } from '@/api/auth/product-registrations/product-registrations';
import { useGetOrgSettings } from '@/api/control/control-plane/control-plane';
import type { OrgSettingsDto } from '@/api/control/missionImpactAPI.schemas';
import { logger } from '@/services/logger';
import { isPendingInvitation, isSelectableMembership, resolveSelectableOrgId } from '@/lib/orgMembership';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const SELECTED_ORG_STORAGE_KEY = 'datamission.selectedOrgId';

interface OrgsContextType {
  organizations: OrganizationMembership[];
  activeOrgs: OrganizationMembership[];
  pendingInvites: OrganizationMembership[];
  selectedOrganizationId: string | null;
  selectedOrganization: OrganizationMembership | null;
  setSelectedOrganizationId: (orgId: string | null) => void;
  isLoading: boolean;
  orgSettings: OrgSettingsDto | null;
  isLoadingOrgSettings: boolean;
  acceptInvitation: (orgId: string) => Promise<void>;
  declineInvitation: (orgId: string) => Promise<void>;
  isProcessingInvitation: boolean;
  refetch: () => Promise<void>;
}

const OrgsContext = createContext<OrgsContextType | undefined>(undefined);

/**
 * Provider component that manages organization data and selection
 * Must be nested within UserProvider to access authentication context
 */
export function OrgsProvider({ children }: { children: ReactNode }) {
  const { accounts } = useMsal();
  const [selectedOrganizationId, setSelectedOrganizationIdState] = useState<string | null>(null);
  const [isProcessingInvitation, setIsProcessingInvitation] = useState(false);
  const queryClient = useQueryClient();

  const { data: orgsResponse, isLoading, refetch: refetchOrgs } = useGetMeOrganizations();

  const organizations: OrganizationMembership[] = orgsResponse?.data.organizations ?? [];
  const selectedOrganization =
    organizations.find((org: OrganizationMembership) => org.orgId === selectedOrganizationId) ??
    null;
  const account = accounts[0];

  // Compute active orgs and pending invites. Only accepted, unexpired memberships can be selected:
  // the Control Plane returns 403 for Invited, Declined and expired memberships.
  const activeOrgs = useMemo(
    () => organizations.filter((org) => isSelectableMembership(org)),
    [organizations]
  );

  const pendingInvites = useMemo(
    () => organizations.filter((org) => isPendingInvitation(org)),
    [organizations]
  );

  /**
   * Selects an organization, ignoring organizations the user can't work in yet.
   */
  const setSelectedOrganizationId = useCallback(
    (orgId: string | null) => {
      if (orgId !== null && !organizations.some((org) => org.orgId === orgId && isSelectableMembership(org))) {
        return;
      }
      setSelectedOrganizationIdState(orgId);
    },
    [organizations]
  );

  // Load org settings for the selected organization
  const { data: orgSettingsResponse, isLoading: isLoadingOrgSettings } = useGetOrgSettings(
    selectedOrganizationId ?? '',
    {
      query: {
        enabled: !!selectedOrganizationId,
      },
    }
  );
  const orgSettings = orgSettingsResponse?.data ?? null;

  /**
   * Get cache key for product registration per org
   */
  const getProductRegistrationCacheKey = (orgId: string) => {
    return `datamission_${account.homeAccountId}_${orgId}_product_registered`;
  };

  /**
   * Check if product has been registered for a specific org
   */
  const isProductRegistered = (orgId: string) => {
    const key = getProductRegistrationCacheKey(orgId);
    const value = localStorage.getItem(key);
    return value === 'true';
  };

  /**
   * Register product for organizations where user is an administrator
   */
  const registerAdminOrganizations = async () => {
    if (organizations.length === 0) return;

    // Filter for admin organizations
    const adminOrgs = organizations.filter(
      (org) => org.role === OrganizationMembershipRole.Administrator
    );

    // Register each admin org that hasn't been registered yet
    for (const org of adminOrgs) {
      if (!isProductRegistered(org.orgId)) {
        try {
          await postOrganizationProductRegistrations(org.orgId, 'data-mission');
          const cacheKey = getProductRegistrationCacheKey(org.orgId);
          localStorage.setItem(cacheKey, 'true');
        } catch (err) {
          logger.warn(`Product registration failed for org ${org.orgId}`, {
            context: 'OrgsContext.registerAdminOrganizations',
            orgId: org.orgId,
            error: err instanceof Error ? err.message : String(err),
          });
          // Continue with other orgs even if one fails
        }
      }
    }
  };

  // Register product for admin organizations when organizations load
  useEffect(() => {
    if (organizations.length > 0) {
      void registerAdminOrganizations();
    }
  }, [organizations.length, account.homeAccountId]);

  // Set the initial selected organization when organizations load, and move off the selected
  // organization if it stops being selectable (e.g. the membership expired).
  useEffect(() => {
    if (organizations.length === 0) return;

    const currentIsSelectable =
      !!selectedOrganizationId && activeOrgs.some((org) => org.orgId === selectedOrganizationId);
    if (currentIsSelectable) return;

    // Prefer the last selected org from localStorage, but only if it is still selectable
    const cachedOrgId = localStorage.getItem(SELECTED_ORG_STORAGE_KEY);
    const orgToSelect = resolveSelectableOrgId(organizations, cachedOrgId);
    if (orgToSelect === selectedOrganizationId) return;

    setSelectedOrganizationIdState(orgToSelect);

    if (orgToSelect) {
      // Track organization selection
      logger.trackEvent('Organization.Selected', {
        orgId: orgToSelect,
        organizationCount: activeOrgs.length,
        wasFromCache: orgToSelect === cachedOrgId,
      });
    }
  }, [organizations, activeOrgs, selectedOrganizationId]);

  // Persist selected organization to localStorage whenever it changes
  useEffect(() => {
    if (selectedOrganizationId) {
      localStorage.setItem(SELECTED_ORG_STORAGE_KEY, selectedOrganizationId);
    }
  }, [selectedOrganizationId]);

  // Keep the telemetry org context in step with the selected organization
  useEffect(() => {
    logger.setOrg(selectedOrganizationId);
  }, [selectedOrganizationId]);

  /**
   * Accept an organization invitation
   */
  const acceptInvitation = useCallback(async (orgId: string) => {
    const invitation = pendingInvites.find((inv) => inv.orgId === orgId);
    if (!invitation) return;

    setIsProcessingInvitation(true);
    try {
      await postMeOrganizationsInvitationsAccept(orgId);

      logger.trackEvent('Organization.Invitation.Accepted', {
        orgId: invitation.orgId,
        orgName: invitation.orgName,
        role: invitation.role,
      });

      toast.success(`You have joined ${invitation.orgName}`);

      // Refresh organizations list
      await queryClient.invalidateQueries({
        queryKey: getGetMeOrganizationsQueryKey(),
      });
    } catch (error) {
      logger.warn('Failed to accept invitation', {
        context: 'OrgsContext.acceptInvitation',
        orgId,
        error: error instanceof Error ? error.message : String(error),
      });

      toast.error('Failed to accept invitation', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
      // eslint-disable-next-line no-restricted-syntax
      throw error;
    } finally {
      setIsProcessingInvitation(false);
    }
  }, [pendingInvites, queryClient]);

  /**
   * Decline an organization invitation
   */
  const declineInvitation = useCallback(async (orgId: string) => {
    const invitation = pendingInvites.find((inv) => inv.orgId === orgId);
    if (!invitation) return;

    setIsProcessingInvitation(true);
    try {
      await postMeOrganizationsInvitationsDecline(orgId);

      logger.trackEvent('Organization.Invitation.Declined', {
        orgId: invitation.orgId,
        orgName: invitation.orgName,
        role: invitation.role,
      });

      toast.success(`Invitation to ${invitation.orgName} has been declined`);

      // Refresh organizations list
      await queryClient.invalidateQueries({
        queryKey: getGetMeOrganizationsQueryKey(),
      });
    } catch (error) {
      logger.warn('Failed to decline invitation', {
        context: 'OrgsContext.declineInvitation',
        orgId,
        error: error instanceof Error ? error.message : String(error),
      });

      toast.error('Failed to decline invitation', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
      // eslint-disable-next-line no-restricted-syntax
      throw error;
    } finally {
      setIsProcessingInvitation(false);
    }
  }, [pendingInvites, queryClient]);

  /**
   * Refetch organizations
   */
  const refetch = useCallback(async () => {
    await refetchOrgs();
  }, [refetchOrgs]);

  return (
    <OrgsContext.Provider
      value={{
        organizations,
        activeOrgs,
        pendingInvites,
        selectedOrganizationId,
        selectedOrganization,
        setSelectedOrganizationId,
        isLoading,
        orgSettings,
        isLoadingOrgSettings,
        acceptInvitation,
        declineInvitation,
        isProcessingInvitation,
        refetch,
      }}
    >
      {children}
    </OrgsContext.Provider>
  );
}

/**
 * Hook to access organizations context
 * @throws Error if used outside of OrgsProvider
 */
export function useOrgs() {
  const context = useContext(OrgsContext);
  if (context === undefined) {
    // eslint-disable-next-line no-restricted-syntax
    throw new Error('useOrgs must be used within an OrgsProvider');
  }
  return context;
}
